
import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, ArrowLeft } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { UserStatusIndicator } from "@/components/UserStatusIndicator";

import MessageInputBar from "@/components/MessageInputBar";
import ServiceHeader from "@/components/ServiceHeader";
import PriceProposalMessage from "@/components/PriceProposalMessage";
import { ConversationFileUpload } from "@/components/ConversationFileUpload";
import { FileMessage } from "@/components/FileMessage";
import { ServiceCompletionReview } from "@/components/ServiceCompletionReview";
const PaymentModal = lazy(() => import("@/components/PaymentModal"));
import PaymentTrigger from "@/components/PaymentTrigger";
import { getUserTimezone, formatTimeWithTimezone, formatShortDateWithTimezone } from "@/utils/timezoneUtils";

interface Message {
  id: string;
  text: string;
  sender_id: string;
  created_at: string;
  is_read: boolean;
  file_url?: string | null;
  file_name?: string | null;
  file_type?: string | null;
  file_size?: number | null;
}

interface ConversationData {
  id: string;
  participant1_id: string;
  participant2_id: string;
  service_id?: string;
  service_started?: boolean;
  service_completed?: boolean;
  final_price?: number | null;
  latest_proposal_price?: number | null;
  latest_proposal_pricing_type?: string | null;
  latest_proposal_by?: string | null;
  price_agreed_by_non_posting_party?: boolean;
  price_confirmed_by_provider?: boolean;
  payment_completed?: boolean;
  participant1?: { first_name: string; last_name: string; avatar_url?: string } | null;
  participant2?: { first_name: string; last_name: string; avatar_url?: string } | null;
  services?: { title: string; price: number; pricing_type: string; user_id: string } | null;
}

interface GroupedMessage {
  id: string;
  senderId: string;
  text: string;
  timestamp: Date;
  isLastInGroup: boolean;
  isFirstInGroup: boolean;
}

interface MessageGroup {
  senderId: string;
  timestamp: Date;
  messages: GroupedMessage[];
}

const ConversationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isOpen } = useSidebar();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [conversation, setConversation] = useState<ConversationData | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasScrolledToBottomRef = useRef(false);

  const scrollToBottom = (instant = false) => {
    if (messagesEndRef.current) {
      // Use scrollIntoView with block: 'nearest' to prevent aggressive scrolling
      const options = instant 
        ? { behavior: "auto" as const, block: "nearest" as const }
        : { behavior: "smooth" as const, block: "nearest" as const };
      
      messagesEndRef.current.scrollIntoView(options);
    }
  };

  // Helper function to detect system messages
  const isSystemMessage = (text: string): boolean => {
    return text.startsWith('[SYSTEM]') || 
           text.startsWith('[PRICE_PROPOSAL]') || 
           text.startsWith('[PRICE_ACCEPTED]') || 
           text.startsWith('[PRICE_REJECTED]');
  };

  // Render system message component
  const renderSystemMessage = (message: Message) => {
    const messageText = message.text;
    
    if (messageText.startsWith('[PRICE_PROPOSAL]')) {
      return (
        <div key={`system-${message.id}`} className="w-full flex justify-center my-4">
          <PriceProposalMessage
            message={messageText}
            senderId={message.sender_id}
            conversationId={id!}
            onUpdate={loadConversation}
          />
        </div>
      );
    }
    
    if (messageText.startsWith('[PRICE_ACCEPTED]') || messageText.startsWith('[PRICE_REJECTED]') || messageText.startsWith('[PRICE_CONFIRMED]')) {
      const isAccepted = messageText.startsWith('[PRICE_ACCEPTED]');
      const isConfirmed = messageText.startsWith('[PRICE_CONFIRMED]');
      const isRejected = messageText.startsWith('[PRICE_REJECTED]');
      const priceMatch = messageText.match(/\$(\d+(?:\.\d{2})?)(\/hour|\/job)?/);
      const price = priceMatch ? priceMatch[1] : '';
      const pricingType = priceMatch && priceMatch[2] ? priceMatch[2] : '';
      const timestamp = formatTimestamp(new Date(message.created_at));
      
      let bgColor = '';
      let displayText = '';
      if (isConfirmed) {
        bgColor = 'bg-blue-100 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100';
        displayText = `✓ Provider confirmed price: $${price}${pricingType}`;
      } else if (isAccepted) {
        bgColor = 'bg-green-100 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-900 dark:text-green-100';
        displayText = `✓ Price agreed: $${price}${pricingType}`;
      } else {
        bgColor = 'bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-900 dark:text-red-100';
        displayText = `✗ Price rejected: $${price}${pricingType}`;
      }
      
      return (
        <div key={`system-${message.id}`} className="w-full flex flex-col items-center my-4">
          <span className="text-xs text-muted-foreground mb-1">{timestamp}</span>
          <div className={`max-w-xs lg:max-w-md px-3 py-2 rounded-lg ${bgColor}`}>
            <p className="text-sm font-medium text-center">{displayText}</p>
          </div>
        </div>
      );
    }
    
    if (messageText.startsWith('[SYSTEM]')) {
      const timestamp = formatTimestamp(new Date(message.created_at));
      return (
        <div key={`system-${message.id}`} className="w-full flex flex-col items-center my-4">
          <span className="text-xs text-muted-foreground mb-1">{timestamp}</span>
          <div className="max-w-xs lg:max-w-md px-3 py-2 rounded-lg bg-primary/10 border border-primary/20 text-foreground">
            <p className="text-sm font-medium text-center">
              {messageText.replace('[SYSTEM] ', '')}
            </p>
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Group messages by sender and time proximity (excluding system messages)
  const groupMessages = (messages: Message[]): MessageGroup[] => {
    // Filter out system messages from grouping
    const regularMessages = messages.filter(msg => !isSystemMessage(msg.text));
    
    if (regularMessages.length === 0) return [];

    const groups: MessageGroup[] = [];
    let currentGroup: MessageGroup | null = null;

    regularMessages.forEach((message, index) => {
      const messageDate = new Date(message.created_at);
      const shouldStartNewGroup = !currentGroup || 
        currentGroup.senderId !== message.sender_id ||
        (messageDate.getTime() - currentGroup.timestamp.getTime()) > 5 * 60 * 1000; // 5 minutes

      if (shouldStartNewGroup) {
        currentGroup = {
          senderId: message.sender_id,
          timestamp: messageDate,
          messages: []
        };
        groups.push(currentGroup);
      }

      const isLastInGroup = index === regularMessages.length - 1 || 
        regularMessages[index + 1].sender_id !== message.sender_id ||
        (new Date(regularMessages[index + 1].created_at).getTime() - messageDate.getTime()) > 5 * 60 * 1000;

      const isFirstInGroup = currentGroup.messages.length === 0;

      currentGroup.messages.push({
        id: message.id,
        senderId: message.sender_id,
        text: message.text,
        timestamp: messageDate,
        isLastInGroup,
        isFirstInGroup
      });
    });

    return groups;
  };

  // Create a timeline that interleaves message groups and system messages
  const createTimeline = (messages: Message[], messageGroups: MessageGroup[]) => {
    const systemMessages = messages.filter(msg => isSystemMessage(msg.text));
    const timeline: Array<{type: 'group' | 'system', data: MessageGroup | Message, timestamp: Date}> = [];

    // Add message groups to timeline
    messageGroups.forEach(group => {
      timeline.push({
        type: 'group',
        data: group,
        timestamp: group.timestamp
      });
    });

    // Add system messages to timeline
    systemMessages.forEach(msg => {
      const timestamp = new Date(msg.created_at);
      timeline.push({
        type: 'system',
        data: msg,
        timestamp
      });
    });

    // Sort by timestamp
    return timeline.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  };

  const formatTimestamp = (timestamp: Date): string => {
    const timezone = getUserTimezone();
    const now = new Date();
    const todayStr = now.toLocaleDateString(undefined, { timeZone: timezone });
    const timestampDateStr = timestamp.toLocaleDateString(undefined, { timeZone: timezone });
    
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toLocaleDateString(undefined, { timeZone: timezone });
    
    const timeStr = timestamp.toLocaleTimeString(undefined, { 
      timeZone: timezone, 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
    
    if (timestampDateStr === todayStr) {
      return timeStr;
    } else if (timestampDateStr === yesterdayStr) {
      return `Yesterday ${timeStr}`;
    } else {
      // Check if within last 7 days
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      if (timestamp > weekAgo) {
        const dayStr = timestamp.toLocaleDateString(undefined, { 
          timeZone: timezone, 
          weekday: 'short' 
        });
        return `${dayStr} ${timeStr}`;
      } else {
        const dateStr = timestamp.toLocaleDateString(undefined, { 
          timeZone: timezone, 
          month: 'short', 
          day: 'numeric' 
        });
        return `${dateStr}, ${timeStr}`;
      }
    }
  };

  const shouldShowTimestamp = (groups: MessageGroup[], groupIndex: number): boolean => {
    if (groupIndex === 0) return true;
    
    const currentGroup = groups[groupIndex];
    const previousGroup = groups[groupIndex - 1];
    
    // Show timestamp if more than 30 minutes between groups
    return (currentGroup.timestamp.getTime() - previousGroup.timestamp.getTime()) > 30 * 60 * 1000;
  };

  useEffect(() => {
    console.log('ConversationDetail mounted, user:', user?.id, 'conversation id:', id);
    
    if (!user) {
      navigate("/auth");
      return;
    }
    if (id) {
      loadConversation();
      loadMessages();
      subscribeToMessages();
    }
  }, [id, user, navigate]);

  useEffect(() => {
    if (messages.length > 0 && !hasScrolledToBottomRef.current) {
      setTimeout(() => {
        scrollToBottom(true);
        hasScrolledToBottomRef.current = true;
      }, 0);
    }
  }, [messages]);

  const loadConversation = async () => {
    try {
      console.log('Loading conversation data for:', id);
      
  const { data: convo, error } = await supabase
    .from('conversations')
    .select('id, last_message, participant1_id, participant2_id, request_status, service_completed, service_started, final_price, service_id, updated_at, latest_proposal_price, latest_proposal_pricing_type, latest_proposal_by, price_agreed_by_non_posting_party, price_confirmed_by_provider, payment_completed')
    .eq('id', id)
    .single();

      if (error || !convo) {
        console.error('Conversation fetch error:', error);
        setAccessDenied(true);
        toast.error("You don't have access to this conversation");
        return;
      }

      console.log('Loaded conversation:', convo);

      // Use secure get_public_profile RPC function to access only safe public fields
      const profilePromises = [convo.participant1_id, convo.participant2_id].map(id =>
        supabase.rpc('get_public_profile', { profile_user_id: id })
      );
      const profileResults = await Promise.all(profilePromises);
      const profiles = profileResults.flatMap(result =>
        result.data && result.data.length > 0 ? result.data : []
      );
      const profErr = profileResults.find(r => r.error)?.error || null;

      if (profErr) {
        console.error('Profile fetch error:', profErr);
      }

      console.log('Loaded profiles:', profiles);

      let serviceData = null;
      if (convo.service_id) {
        const { data: service, error: serviceErr } = await supabase
          .from('services')
          .select('title, price, pricing_type, user_id')
          .eq('id', convo.service_id)
          .single();

        if (serviceErr) {
          console.error('Service fetch error:', serviceErr);
        } else {
          serviceData = service;
          console.log('Loaded service:', serviceData);
        }
      }

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      setConversation({
        ...convo,
        participant1: profileMap.get(convo.participant1_id) || null,
        participant2: profileMap.get(convo.participant2_id) || null,
        services: serviceData
      });
    } catch (error) {
      console.error("Failed to load conversation:", error);
      toast.error("Failed to load conversation details");
    }
  };

  const loadMessages = async () => {
    try {
      console.log('Loading messages for conversation:', id);
      
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      console.log('Loaded messages:', data?.length || 0);
      setMessages(data || []);
    } catch (error) {
      console.error("Failed to load messages:", error);
      const raw = JSON.stringify(error || {}).toLowerCase();
      if (raw.includes("unauthorized") || raw.includes("permission") || raw.includes("row level security") || raw.includes("rls") || raw.includes("not allowed")) {
        setAccessDenied(true);
      }
      toast.error("Failed to load messages");
    } finally {
      setIsLoading(false);
    }
  };

  const subscribeToMessages = () => {
    console.log('Setting up message subscription for conversation:', id);
    
    const messagesChannel = supabase
      .channel(`conversation-messages-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${id}`
        },
        (payload) => {
          console.log('Received new message via subscription:', payload);
          const newMessage = payload.new as Message;
          setMessages(prev => [...prev, newMessage]);

          // Keep header state (Pay/Confirm) in sync by reloading conversation when price-related system messages arrive.
          if (newMessage.text.startsWith('[PRICE_')) {
            loadConversation();
          }

          // Only scroll for non-system messages to prevent layout shifts
          if (!newMessage.text.startsWith('[PRICE_') && !newMessage.text.startsWith('[SYSTEM]')) {
            setTimeout(() => scrollToBottom(false), 100);
          }
        }
      )
      .subscribe();

    // Subscribe to conversation updates for service progress
    const conversationChannel = supabase
      .channel(`conversation-updates-${id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'conversations',
          filter: `id=eq.${id}`
        },
        (payload) => {
          console.log('Received conversation update:', payload);
          loadConversation(); // Reload conversation data
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up subscriptions');
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(conversationChannel);
    };
  };

  const handleNewMessage = async (messageText: string) => {
    if (!user || !id) throw new Error("Missing user or conversation ID");
    
    console.log('Sending message:', messageText);
    
    const { error } = await supabase
      .from('messages')
      .insert({
        conversation_id: id,
        sender_id: user.id,
        text: messageText
      });

    if (error) throw error;
    
    console.log('Message sent successfully');
  };

  const getOtherParticipant = () => {
    if (!conversation || !user) return null;
    
    if (conversation.participant1_id === user.id) {
      return conversation.participant2;
    } else {
      return conversation.participant1;
    }
  };

  const getOtherParticipantId = () => {
    if (!conversation || !user) return null;
    
    if (conversation.participant1_id === user.id) {
      return conversation.participant2_id;
    } else {
      return conversation.participant1_id;
    }
  };

  const otherParticipant = getOtherParticipant();
  const otherParticipantId = getOtherParticipantId();
  const otherParticipantName = otherParticipant 
    ? `${otherParticipant.first_name} ${otherParticipant.last_name}`
    : "Unknown User";
  const otherParticipantInitials = otherParticipant
    ? `${otherParticipant.first_name?.[0] || ''}${otherParticipant.last_name?.[0] || ''}`
    : "U";
  const otherParticipantAvatar = otherParticipant?.avatar_url;

  console.log('Rendering ConversationDetail - other participant:', otherParticipantId, otherParticipantName);

  if (!user) {
    return null;
  }

  if (accessDenied) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-background">
        <div className="text-center p-6 border border-border rounded-lg bg-card">
          <p className="text-foreground mb-2 font-semibold">You don't have access to this conversation.</p>
          <Button variant="outline" onClick={() => navigate('/conversations')}>Go back to conversations</Button>
        </div>
      </div>
    );
  }

  const messageGroups = groupMessages(messages);
  
  // Sort all messages chronologically
  const sortedMessages = [...messages].sort((a, b) => {
    const aTime = new Date(a.created_at).getTime();
    const bTime = new Date(b.created_at).getTime();
    return aTime - bTime;
  });

  return (
    <div className="h-screen w-full flex flex-col bg-background overflow-hidden pt-3">
      {/* Header - Fixed height with proper top spacing */}
      <div className="flex-shrink-0 bg-card border-b border-border px-4 py-3">
        <Button 
          variant="outline" 
          onClick={() => navigate("/conversations")}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Conversations
        </Button>
        
        <ServiceHeader
          recipient={{
            id: otherParticipantId || '',
            name: otherParticipantName,
            avatarInitials: otherParticipantInitials
          }}
          conversation={conversation}
          isProvider={user?.id === conversation?.participant2_id}
          isClient={user?.id === conversation?.participant1_id}
          onUpdate={loadConversation}
          onServiceCompleted={() => setShowReviewModal(true)}
        />
      </div>

      {/* Service controls now in header - ServiceProgressPanel removed */}

      {/* Chat container - Flexible height between header and input */}
      <div className="flex-1 flex flex-col min-h-0 rounded-lg shadow-sm border border-border overflow-hidden bg-background">
        {/* Messages area - Flexible height with stable bottom padding for fixed input */}
        <div 
          className="flex-1 px-4 overflow-y-auto min-h-0 bg-background" 
          style={{ paddingBottom: '180px' }}
          data-messages-container
        >
          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <p className="text-muted-foreground">Loading messages...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <p className="text-muted-foreground">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            <div className="py-4 space-y-4 bg-background">
              {sortedMessages.map((message, index) => {
                // Check if this is a system message
                if (isSystemMessage(message.text)) {
                  return renderSystemMessage(message);
                }
                
                // Regular message rendering logic
                const isFromUser = message.sender_id === user.id;
                const hasFile = message.file_url && message.file_name;
                
                const messageTime = new Date(message.created_at);
                const prevMessage = index > 0 ? sortedMessages[index - 1] : null;
                const showMessageTimestamp = !prevMessage || 
                  isSystemMessage(prevMessage.text) ||
                  prevMessage.sender_id !== message.sender_id ||
                  (messageTime.getTime() - new Date(prevMessage.created_at).getTime()) > 5 * 60 * 1000;
                
                return (
                  <div key={message.id} className={`flex flex-col ${isFromUser ? 'items-end' : 'items-start'} mb-1`}>
                    {showMessageTimestamp && (
                      <span className={`text-xs text-muted-foreground mb-1 ${isFromUser ? 'mr-2' : 'ml-2'}`}>
                        {formatTimestamp(messageTime)}
                      </span>
                    )}
                    <div
                      className={`max-w-xs lg:max-w-md px-4 py-2.5 rounded-2xl ${
                        isFromUser
                          ? 'bg-primary text-primary-foreground shadow-md'
                          : 'bg-muted text-foreground border border-border shadow-sm'
                      } transition-all duration-200 hover:shadow-lg relative`}
                    >
                      {hasFile && (
                        <FileMessage
                          fileUrl={message.file_url!}
                          fileName={message.file_name!}
                          fileType={message.file_type || 'application/octet-stream'}
                          fileSize={message.file_size || 0}
                        />
                      )}
                      {message.text && <p className="font-inter text-sm leading-relaxed">{message.text}</p>}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>
      
      {/* Message Input Bar - Fixed to bottom, respecting sidebar */}
      <div className={`fixed bottom-0 left-0 z-50 bg-background border-t border-border transition-all duration-300 ease-in-out ${isOpen ? 'w-[calc(100vw-80px)]' : 'w-full'}`}>
        <MessageInputBar 
          onSendMessage={handleNewMessage} 
          disabled={!user}
          placeholder="Type your message..."
        />
      </div>

      {/* Payment Modal */}
      {showPaymentModal && conversation && (
        <Suspense fallback={null}>
          <PaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            providerId={conversation.participant2_id}
            providerName={otherParticipantName}
            amount={conversation.final_price}
            conversationId={conversation.id}
            onPaymentSuccess={() => {
              loadConversation();
              setShowPaymentModal(false);
            }}
          />
        </Suspense>
      )}

      {/* Review Modal - Shows after client marks service complete */}
      {showReviewModal && conversation && conversation.service_id && (
        <ServiceCompletionReview
          open={showReviewModal}
          onOpenChange={setShowReviewModal}
          serviceId={conversation.service_id}
          providerId={conversation.services?.user_id || conversation.participant2_id}
          providerName={otherParticipantName}
          providerAvatarUrl={otherParticipantAvatar}
          serviceTitle={conversation.services?.title || 'Service'}
          onReviewSubmitted={() => {
            loadConversation();
          }}
        />
      )}
    </div>
  );
};

export default ConversationDetail;
